import {
  ExpressionReturnType,
  getSubqueryParameterType,
  identifierRegex,
  parseAndValidateReportExpression,
  ReportFilter,
  ReportParameter,
  wellKnownProperties
} from '@cumu/shared';
import { get } from 'jsonpointer';
import { parseReportDefinition } from './report-definition';

export function getReportParameters(
  input: HTMLInputElement
): ReportParameter[] {
  return getInputParameters(input).filteredParameters;
}

export function validateReportParameterReferences(
  input: HTMLInputElement,
  values: string[]
): { valid: true } | { valid: false; message: string } {
  for (const value of values) {
    if (!identifierRegex.test(value)) {
      continue;
    }
    const { parameters, filteredParameters, hint } = getInputParameters(input);
    if (!parameters.some(p => p.name === value)) {
      return {
        valid: false,
        message: `Invalid parameter reference. Unknown parameter "${value}".`
      };
    }
    if (!filteredParameters.some(p => p.name === value)) {
      return {
        valid: false,
        message: `Invalid parameter reference.${hint ? ` Parameter "${value}" is not of type ${hint}.` : ''}`
      };
    }
  }
  return { valid: true };
}

function getInputParameters(input: HTMLInputElement) {
  // todo: request via event.
  if (!input.form) {
    throw new Error('Input is not form associated.');
  }
  const { definition, properties } = parseReportDefinition(input.form);
  const { columns, parameters } = definition;
  // parameters are used in filters, so we need to know the type of filter to determine which types of parameters are valid.
  const filterPointer = '/' + input.name.split('/').slice(0, -1).join('/');
  const filter = get({ definition }, filterPointer) as ReportFilter | undefined;
  if (!filter) {
    throw new Error(
      `Unable to locate filter associated with parameter reference "${filterPointer}".`
    );
  }
  const parameterTypes: ReportParameter['type'][] = [];
  const types: ExpressionReturnType[] = [];
  let hint: string | undefined = undefined;
  let property_id: number | undefined;
  if (filter.type === 'standard') {
    property_id = filter.property_id;
  } else if (
    filter.aggregate === 'min' ||
    filter.aggregate === 'max' ||
    filter.aggregate === 'last-created' ||
    filter.aggregate === 'last-started' ||
    filter.aggregate === 'last-ended'
  ) {
    property_id = filter.subquery_property_id;
  } else {
    parameterTypes.push('literal-number', 'select-number');
    types.push('int', 'decimal');
    hint = 'number';
  }
  if (property_id) {
    const property = properties.find(p => p.property_id === property_id);
    if (!property) {
      throw new Error(
        `Unable to locate property associated with filter "${filterPointer}" with property_id ${property_id}.`
      );
    }
    switch (property.type) {
      case 'currency':
      case 'decimal':
      case 'int':
        parameterTypes.push('literal-number', 'select-number');
        types.push('int', 'decimal');
        hint = 'number';
        break;
      case 'date':
        parameterTypes.push('literal-date', 'select-date');
        types.push('date', 'datetime');
        hint = 'date';
        break;
      case 'uniqueidentifier':
        switch (property.property_id) {
          case wellKnownProperties.person.create_organization_id:
          case wellKnownProperties.person.update_organization_id:
          case wellKnownProperties.user_organization.organization_id:
          case wellKnownProperties.activity.create_organization_id:
          case wellKnownProperties.activity.update_organization_id:
          case wellKnownProperties.activity.assignee_organization_id:
          case wellKnownProperties.organization_project.organization_id:
            parameterTypes.push('organization');
            hint = 'organization';
            break;
          case wellKnownProperties.person.user_id:
          case wellKnownProperties.person.create_user_id:
          case wellKnownProperties.person.update_user_id:
          case wellKnownProperties.activity.create_user_id:
          case wellKnownProperties.activity.update_user_id:
          case wellKnownProperties.activity.assignee_user_id:
          case wellKnownProperties.activity.comment_create_user_id:
          case wellKnownProperties.activity.comment_mentioned_user_id:
            parameterTypes.push('user');
            hint = 'user';
            break;
        }
        break;
    }
  }

  const filteredParameters: ReportParameter[] = [];
  for (const parameter of parameters) {
    if (parameterTypes.includes(parameter.type)) {
      filteredParameters.push(parameter);
    } else if (parameter.type === 'expression' && types.length) {
      const result = parseAndValidateReportExpression({
        name: parameter.name,
        expression: parameter.expression,
        getDefinitionInfo: () => ({ columns, parameters, properties })
      });
      if (result.valid && types.includes(result.type)) {
        filteredParameters.push(parameter);
      }
    } else if (parameter.type === 'subquery' && types.length) {
      const { type } = getSubqueryParameterType(parameter, properties);
      if (type && types.includes(type)) {
        filteredParameters.push(parameter);
      }
    }
  }

  return {
    parameterTypes,
    types,
    hint,
    definition,
    properties,
    parameters,
    filteredParameters
  };
}
